import { setActiveAwsReviewStatus } from "./awsOpportunityUpdateRules";

const isOpportunityRequest = (input: RequestInfo | URL): boolean => {
  const url =
    typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

  return url.includes("/api/");
};

type OpportunityLikeRecord = Record<string, unknown> & { id?: unknown };

const normalizeOpportunityRecord = (value: unknown): unknown => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }

  const record = value as Record<string, unknown>;
  let changed = false;
  const normalized: Record<string, unknown> = { ...record };

  if (record.nextStepsHistory === null) {
    normalized.nextStepsHistory = [];
    changed = true;
  }

  if (normalized.awsOpp == null && record.awsOppId != null) {
    normalized.awsOpp = record.awsOppId;
    changed = true;
  }

  if (normalized.partnerCrmUnique == null && record.partnerCrmUniqueId != null) {
    normalized.partnerCrmUnique = record.partnerCrmUniqueId;
    changed = true;
  }

  return changed ? normalized : value;
};

// Capture the review status of the record opened in the gruim edit view.
const captureActiveReviewStatus = (value: unknown): void => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return;
  const record = value as OpportunityLikeRecord;
  if (record.id != null && "reviewStatus" in record) {
    setActiveAwsReviewStatus(String(record.reviewStatus ?? ""));
  }
};

const normalizeOpportunityPayload = (payload: unknown): { value: unknown; changed: boolean } => {
  if (Array.isArray(payload)) {
    setActiveAwsReviewStatus("");
    let changed = false;
    const normalized = payload.map((item) => {
      const result = normalizeOpportunityRecord(item);
      changed = changed || result !== item;
      return result;
    });

    return { value: changed ? normalized : payload, changed };
  }

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;

    if (record.obj && typeof record.obj === "object" && !Array.isArray(record.obj)) {
      captureActiveReviewStatus(record.obj);
      const normalizedObj = normalizeOpportunityRecord(record.obj);
      if (normalizedObj !== record.obj) {
        return { value: { ...record, obj: normalizedObj }, changed: true };
      }
    }

    if (record.data && typeof record.data === "object" && !Array.isArray(record.data)) {
      captureActiveReviewStatus(record.data);
      const normalizedData = normalizeOpportunityRecord(record.data);
      if (normalizedData !== record.data) {
        return { value: { ...record, data: normalizedData }, changed: true };
      }
    }
  }

  captureActiveReviewStatus(payload);
  const normalized = normalizeOpportunityRecord(payload);
  return { value: normalized, changed: normalized !== payload };
};

export const installOpportunityFetchNormalizer = (): (() => void) => {
  if (typeof window === "undefined") return () => undefined;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch(input, init);

    if (!isOpportunityRequest(input)) {
      return response;
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      return response;
    }

    let payload: unknown;
    try {
      payload = await response.clone().json();
    } catch {
      return response;
    }

    const normalized = normalizeOpportunityPayload(payload);
    if (!normalized.changed) {
      return response;
    }

    return new Response(JSON.stringify(normalized.value), {
      status: response.status,
      statusText: response.statusText,
      headers: new Headers(response.headers),
    });
  }) as typeof window.fetch;

  return () => {
    window.fetch = originalFetch;
  };
};
